import { GlobalStateController } from "bai-react-global-state";
import _ from "lodash";
import React, { useEffect, useState } from "react";

import {
    ICollaborationInvite,
    IWorkspacePermission,
    IWorkspacePermissionAccess,
    IWorkspaceResourcePresentation,
    IWorkspaceResourcePresentationTemplate,
    WorkspacePermissionSubjectType,
    WorkspaceResourceType
} from "common/interfaces";
import { getSequentialRunner, SequentialRunner } from "common/utils/runSequentially";
import WorkspaceController, {
    getCollaborationInvitesToResource,
    getWorkspacePermissionsToResource,
    getWorkspaceResource
} from "js/controllers/WorkspaceController";
import getLogger, { LogGroup } from "js/core/logger";
import { ClipboardType, clipboardWrite } from "js/core/utilities/clipboard";
import { trackActivity } from "js/core/utilities/utilities";
import PresentationEditorController from "js/editor/PresentationEditor/PresentationEditorController";
import { auth } from "js/firebase/auth";
import { app } from "js/namespaces";
import Spinner from "js/react/components/Spinner";

const logger = getLogger(LogGroup.BILLING);

declare global {
    interface Window {
        serverUrl: string;
    }
}

export interface PresentationPermissionsControllerState {
    initialized: boolean;
    initializeError: Error | null;
    presentationId: string;
    workspaceId: string;
    permissions: IWorkspacePermission[];
    invites: ICollaborationInvite[];
    resource: IWorkspaceResourcePresentation | IWorkspaceResourcePresentationTemplate
}

const initialState: PresentationPermissionsControllerState = {
    initialized: false,
    initializeError: null,
    presentationId: null,
    workspaceId: null,
    permissions: [],
    invites: [],
    resource: null
};

class PresentationPermissionsController extends GlobalStateController<PresentationPermissionsControllerState> {
    private _runSequentially: SequentialRunner;
    private _stateDidUpdateCallbacks: ((prevState: PresentationPermissionsControllerState, currentState: PresentationPermissionsControllerState) => void)[] = [];

    public get initialized() {
        return this._state.initialized;
    }

    public get presentationId() {
        return this._state.presentationId;
    }

    public get workspaceId() {
        return this._state.workspaceId;
    }

    public get permissions() {
        return this._state.permissions;
    }

    public get invites() {
        return this._state.invites;
    }

    constructor() {
        super(_.cloneDeep(initialState));
        this._runSequentially = getSequentialRunner();
    }

    public async initialize(presentationId: string, workspaceId: string) {
        await this._runSequentially(async () => {
            logger.info("[PresentationPermissionsController] initialize()", { presentationId });

            try {
                await this._updateState({ ..._.cloneDeep(initialState), presentationId, workspaceId });

                await this._fetchData();

                await this._updateState({ initialized: true });
            } catch (err) {
                logger.error(err, "[PresentationPermissionsController] initialize() failed");

                await this._updateState({
                    ..._.cloneDeep(initialState),
                    presentationId,
                    workspaceId,
                    initialized: false,
                    initializeError: err
                });
            }
        });
    }

    public async waitForAsyncActions() {
        return this._runSequentially(async () => { });
    }

    /**
    * Registers a callback to be called after the state of the WorkspaceController changes.
    * @param callback - The callback to be called when the state changes.
    * @returns A function to remove the callback.
    */
    public onStateDidUpdate(callback: (prevState: PresentationPermissionsControllerState, currentState: PresentationPermissionsControllerState) => void) {
        this._stateDidUpdateCallbacks.push(callback);
        return () => {
            this._stateDidUpdateCallbacks = this._stateDidUpdateCallbacks.filter(cb => cb !== callback);
        };
    }

    private async _fetchData() {
        const { workspaceId, presentationId } = this._state;

        const resource = await getWorkspaceResource({ workspaceId, resourceId: presentationId }) as IWorkspaceResourcePresentation | IWorkspaceResourcePresentationTemplate;
        if (!resource) {
            throw new Error("Presentation resource not found");
        }

        const [permissions, invites] = await Promise.all([
            getWorkspacePermissionsToResource({ workspaceId, resourceId: resource.resourceId, resourceType: resource.resourceType }),
            resource.resourceType === WorkspaceResourceType.PRESENTATION
                ? getCollaborationInvitesToResource({ workspaceId, resourceId: resource.resourceId, resourceType: resource.resourceType })
                : Promise.resolve([]),
        ]);

        await this._updateState({ permissions, invites, resource });
    }

    protected _stateDidUpdate(prevState: PresentationPermissionsControllerState) {
        try {
            this._stateDidUpdateCallbacks.forEach(callback => callback(prevState, this._state));
        } catch (err) {
            logger.error(err, "[PresentationPermissionsController] _stateDidUpdate() failed to run stateDidUpdate callbacks");
        }
    }

    /**
     * Adds current presentation to the given folder with the given permissions
     */
    public async addPresentationPermissionsForFolder({
        folderId,
        presentationPermissions,
        analyticsProps
    }: {
        folderId: string,
        presentationPermissions: Omit<IWorkspacePermissionAccess, "owner">,
        analyticsProps?: {
            location?: string,
        }
    }) {
        await this._runSequentially(async () => {
            const { resource: { resourceId, resourceType } } = this._state;

            await WorkspaceController.updateWorkspaceResourceFolderResources({
                folderId,
                update: {
                    add: [{ resourceId, resourceType, permissions: presentationPermissions }]
                }
            });
            await this._fetchData();

            /// Legacy analytics
            const props = {
                presentation_id: resourceId,
                old_value: null,
                new_value: presentationPermissions.write.object ? "write" : "read",
                team_folders: [{ id: folderId, name: WorkspaceController.resourceFolders.find(f => f.id === folderId)?.name }],
                recipient_emails: [],
                sender_email: auth().currentUser.email,
                location: analyticsProps?.location,
            };
            trackActivity("Collab", "Invite", null, null, props, { audit: true });
            ////////////////////
        });
    }

    /**
     * Updates current presentation permissions in the given folder
     */
    public async updatePresentationPermissionsForFolder({
        folderId,
        presentationPermissions
    }: {
        folderId: string,
        presentationPermissions: Omit<IWorkspacePermissionAccess, "owner">
    }) {
        await this._runSequentially(async () => {
            const { resource: { resourceId, resourceType } } = this._state;

            await WorkspaceController.updateWorkspaceResourceFolderResources({
                folderId,
                update: {
                    update: [{ resourceId, resourceType, permissions: presentationPermissions }]
                }
            });
            await this._fetchData();
        });
    }

    /**
     * Removes current presentation from the given folder
     */
    public async removePresentationPermissionsForFolder({
        folderId,
    }: {
        folderId: string,
    }) {
        await this._runSequentially(async () => {
            const { resource: { resourceId, resourceType } } = this._state;

            await WorkspaceController.updateWorkspaceResourceFolderResources({
                folderId,
                update: {
                    remove: [{ resourceId, resourceType }]
                }
            });
            await this._fetchData();
        });
    }

    public async addPresentationPermissionsForUsers({
        users,
        analyticsProps
    }: {
        users: { uid?: string, email?: string, permissions: IWorkspacePermissionAccess }[],
        analyticsProps?: {
            isInviteResend?: boolean,
            location?: string,
        }
    }) {
        await this._runSequentially(async () => {
            const { resource: { resourceId, resourceType } } = this._state;

            const { permissions, invites } = await WorkspaceController.updateWorkspaceResourceUsers({
                resourceId,
                resourceType,
                update: {
                    add: users.map(user => ({
                        subjectId: user.uid,
                        subjectEmail: user.email,
                        subjectType: WorkspacePermissionSubjectType.USER,
                        permissions: user.permissions
                    }))
                }
            });
            await this._updateState({ permissions, invites });

            // Hack we have to do before we migrate
            if (users.some(user => user.permissions.owner) && PresentationEditorController.isInitialized) {
                PresentationEditorController.reloadPresentation();
            }

            /// Legacy analytics
            if (analyticsProps?.isInviteResend) {
                users.forEach(user => {
                    const props = {
                        presentation_id: resourceId,
                        permission: user.permissions.write.object ? "write" : "read",
                        recipient_email: user.email,
                        sender_email: auth().currentUser.email,
                    };
                    trackActivity("Collab", "ResendInvite", null, null, props, { audit: true });
                });
            } else {
                const emails = users.map(user => user.email);
                const permission = users[0].permissions.write.object ? "write" : "read";

                const props = {
                    presentation_id: resourceId,
                    old_value: null,
                    new_value: permission,
                    team_folders: [],
                    recipient_emails: emails,
                    sender_email: auth().currentUser.email,
                    location: analyticsProps?.location
                };
                trackActivity("Collab", "Invite", null, null, props, { audit: true });
            }
            ////////////////////
        });
    }

    public async updatePresentationPermissionsForUsers({
        users
    }: {
        users: { uid?: string, email?: string, permissions: IWorkspacePermissionAccess }[],
    }) {
        await this._runSequentially(async () => {
            const { resource: { resourceId, resourceType } } = this._state;

            const permissionsBefore = _.cloneDeep(this._state.permissions);
            const invitesBefore = _.cloneDeep(this._state.invites);

            const { permissions, invites } = await WorkspaceController.updateWorkspaceResourceUsers({
                resourceId,
                resourceType,
                update: {
                    add: users.map(user => ({
                        subjectId: user.uid,
                        subjectEmail: user.email,
                        subjectType: WorkspacePermissionSubjectType.USER,
                        permissions: user.permissions
                    }))
                }
            });
            await this._updateState({ permissions, invites });

            // Hack we have to do before we migrate
            if (users.some(user => user.permissions.owner) && PresentationEditorController.isInitialized) {
                PresentationEditorController.reloadPresentation();
            }

            /// Legacy analytics
            users.forEach(user => {
                const permissionBefore = permissionsBefore.find(p => p.subjectId === user.uid);
                const inviteBefore = invitesBefore.find(i => i.inviteeEmail === user.email);
                const oldValue = (permissionBefore?.write.object ?? inviteBefore?.permissions.write ?? false) ? "Edit" : "View";

                const props = {
                    presentation_id: resourceId,
                    old_value: oldValue,
                    new_value: user.permissions.write.object ? "Edit" : "View",
                    recipient_pending: !user.uid,
                    recipient_email: user.email
                };
                trackActivity("Collab", "AccessChanged", null, null, props);
            });
            ////////////////////
        });
    }

    public async removePresentationPermissionsForUsers({
        users
    }: {
        users: { uid?: string, email?: string }[],
    }) {
        await this._runSequentially(async () => {
            const { resource: { resourceId, resourceType } } = this._state;

            await WorkspaceController.updateWorkspaceResourceUsers({
                resourceId,
                resourceType,
                update: { remove: users.map(user => ({ subjectId: user.uid, subjectEmail: user.email, subjectType: WorkspacePermissionSubjectType.USER })) }
            });
            await this._fetchData();
        });
    }

    private _reset(runSequentially = true) {
        const reset = async () => {
            logger.info("[PresentationPermissionsController] reset()");

            await this._updateState(_.cloneDeep(initialState));
        };

        if (runSequentially) {
            return this._runSequentially(reset);
        }

        return reset();
    }

    public reset() {
        return this._reset();
    }

    /**
     * Wraps the given component with a state injector that renders the component only if the controller
     * is initialized.
     *
     * !! THIS WRAPPER WILL *NOT* INITIALIZE THE CONTROLLER IF IT'S NOT ALREADY INITIALIZED !!
     */
    public withInitializedState<T extends React.ComponentType<any>>(Component: T, PreloadComponent: React.ComponentType<any> = Spinner, ErrorComponent: React.ComponentType<any> = () => null) {
        function Wrapper(props: React.ComponentProps<T>) {
            const { initialized, initializeError } = props;

            if (initializeError) {
                return <ErrorComponent {...props} />;
            }

            if (!initialized) {
                return <PreloadComponent {...props} />;
            }

            return <Component {...props} />;
        }

        return this.withState(Wrapper);
    }

    /**
     * Wraps the given component with a state injector that ensures the controller is initialized for
     * the presentation id provided by the getPresentationId function. The provided component will be
     * rendered only when the controller is initialized for the correct presentation.
     * Allows seamless controller initialization.
     *
     * !! THIS WRAPPER *WILL* INITIALIZE THE CONTROLLER IF NEEDED !!
     */
    public withStateInitializer<T extends React.ComponentType<any>>(Component: T, getPresentationId: (props: React.ComponentProps<T>) => string, resetOnUnmount: boolean = false) {
        const presentationPermissionsController = this;

        const ComponentWithInitializedState = presentationPermissionsController.withState(Component);

        const Initializer = WorkspaceController.withInitializedState(presentationPermissionsController.withState(
            function Initializer({ children, requestedPresentationId }: { children: React.ReactElement<any, any>, requestedPresentationId: string }) {
                const [renderChildren, setRenderChildren] = useState(false);

                const presentationId = presentationPermissionsController.presentationId;
                const workspaceId = presentationPermissionsController.workspaceId;
                const initialized = presentationPermissionsController.initialized;
                const requestedWorkspaceId = WorkspaceController.workspaceId;

                useEffect(() => {
                    if (presentationId === requestedPresentationId && workspaceId === requestedWorkspaceId) {
                        if (initialized) {
                            setRenderChildren(true);
                        }

                        return;
                    }

                    setRenderChildren(false);

                    presentationPermissionsController.initialize(requestedPresentationId, requestedWorkspaceId);
                }, [initialized, presentationId, requestedPresentationId, workspaceId, requestedWorkspaceId]);

                useEffect(() => {
                    return () => {
                        if (resetOnUnmount) {
                            presentationPermissionsController.reset();
                        }
                    };
                }, []);

                if (renderChildren) {
                    return children;
                }

                return <Spinner />;
            }
        ));

        // Use forwardRef because ShowDialog passes a ref to dialog components, but function components
        // cannot receive refs directly. This forwards the ref to the underlying class component.
        return React.forwardRef<any, React.ComponentProps<T>>(function PresentationPermissionsControllerInitializer(props, ref) {
            const requestedPresentationId = getPresentationId(props);
            if (!requestedPresentationId) {
                return null;
            }

            return (
                <Initializer requestedPresentationId={requestedPresentationId}>
                    <ComponentWithInitializedState {...props} ref={ref} />
                </Initializer>
            );
        });
    }
}

const presentationPermissionsController = new PresentationPermissionsController();

export default presentationPermissionsController;

// for lazy import and debug
app.presentationPermissionsController = presentationPermissionsController;

export async function copyCollaborationInviteUrlToClipboard(invite: ICollaborationInvite) {
    const presentationUrl = invite.permissions.write ? `${window.serverUrl}/${invite.presentationId}/1` : `${window.serverUrl}/player/${invite.presentationId}`;
    const inviteUrl = `${window.serverUrl}/invite/${invite.id}?redirect=${presentationUrl}`;

    await clipboardWrite({
        [ClipboardType.TEXT]: inviteUrl,
    });

    /// Legacy analytics
    const props = {
        presentation_id: invite.presentationId,
        permission: invite.permissions.write ? "write" : "read",
        recipient_email: invite.inviteeEmail,
        sender_email: auth().currentUser.email,
        inviteUrl,
    };
    trackActivity("Collab", "CopyInviteLink", null, null, props, { audit: true });
    ////////////////////
}
